import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Eye, Edit3, Search, Tag, Variable, Send, Workflow, Wand2 } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const CATEGORY_LABEL = {
  compose: "Composer",
  sequence: "Secuencias",
  both: "Ambos",
};
const CATEGORY_ICON = {
  compose: Send,
  sequence: Workflow,
  both: Tag,
};

/**
 * TemplatesTab — gestión CRUD de plantillas de email reutilizables.
 * Endpoints: /api/admin/emails/templates (GET, POST, PATCH, DELETE, /duplicate, /preview).
 */
export default function TemplatesTab() {
  const [view, setView] = useState("templates"); // "templates" | "parts"
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState({ header: [], footer: [] });
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterActive, setFilterActive] = useState("all"); // all|active|inactive
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null); // null = no modal abierto; "new" = creación
  const [previewing, setPreviewing] = useState(null); // template object
  const [deleting, setDeleting] = useState(null); // template object

  const loadParts = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/emails/parts");
      const byKind = { header: [], footer: [] };
      (data.items || []).forEach((p) => {
        if (p.kind === "header" || p.kind === "footer") byKind[p.kind].push(p);
      });
      setParts(byKind);
    } catch {
      // silencioso
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory !== "all") params.set("category", filterCategory);
      if (filterActive === "active") params.set("is_active", "true");
      if (filterActive === "inactive") params.set("is_active", "false");
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/admin/emails/templates?${params}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando plantillas");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterActive, q]);

  useEffect(() => { load(); loadParts(); }, [load, loadParts]);

  const duplicate = async (t) => {
    try {
      const { data } = await api.post(`/admin/emails/templates/${t.id}/duplicate`);
      toast.success(`Plantilla duplicada como "${data.name}"`);
      await load();
      setEditingId(data.id);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error duplicando");
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/emails/templates/${deleting.id}`);
      toast.success("Plantilla eliminada");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error eliminando");
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/admin/emails/templates/${t.id}`, { is_active: !t.is_active });
      toast.success(t.is_active ? "Plantilla desactivada" : "Plantilla activada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error actualizando");
    }
  };

  return (
    <div data-testid="email-templates-tab">
      {/* Iter14 — Sub-tabs Templates / Parts */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        <button
          onClick={() => setView("templates")}
          className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
            view === "templates"
              ? "text-brand-burgundy border-b-2 border-brand-burgundy -mb-px font-medium"
              : "text-gray-500 hover:text-gray-900"
          }`}
          data-testid="templates-subnav-templates"
        >
          Plantillas
        </button>
        <button
          onClick={() => setView("parts")}
          className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
            view === "parts"
              ? "text-brand-burgundy border-b-2 border-brand-burgundy -mb-px font-medium"
              : "text-gray-500 hover:text-gray-900"
          }`}
          data-testid="templates-subnav-parts"
        >
          Encabezados y pies
        </button>
      </div>

      {view === "parts" && (
        <PartsManager onReload={loadParts} />
      )}

      {view === "templates" && (<>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Buscar</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="nombre, descripción, asunto…"
              className="rounded-sm h-9 pl-8"
              data-testid="templates-search"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Categoría</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="rounded-sm h-9 w-[150px]" data-testid="templates-filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="compose">Composer</SelectItem>
              <SelectItem value="sequence">Secuencias</SelectItem>
              <SelectItem value="both">Ambos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Estado</Label>
          <Select value={filterActive} onValueChange={setFilterActive}>
            <SelectTrigger className="rounded-sm h-9 w-[130px]" data-testid="templates-filter-active">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="active">Activas</SelectItem>
              <SelectItem value="inactive">Inactivas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white rounded-sm h-9"
          onClick={() => setEditingId("new")}
          data-testid="template-new-button"
        >
          <Plus className="w-4 h-4 mr-1" /> Nueva plantilla
        </Button>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
              <th className="px-3 py-3 text-left">Nombre</th>
              <th className="px-3 py-3 text-left">Categoría</th>
              <th className="px-3 py-3 text-left">Asunto</th>
              <th className="px-3 py-3 text-left">Variables</th>
              <th className="px-3 py-3 text-right">Usos</th>
              <th className="px-3 py-3 text-center">Activa</th>
              <th className="px-3 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-500" data-testid="templates-empty">
                  <p className="mb-2">No hay plantillas creadas.</p>
                  <button
                    onClick={() => setEditingId("new")}
                    className="text-primary hover:underline text-sm"
                  >
                    Crear la primera plantilla →
                  </button>
                </td>
              </tr>
            )}
            {!loading && items.map((t) => {
              const Icon = CATEGORY_ICON[t.category] || Tag;
              return (
                <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`template-row-${t.id}`}>
                  <td className="px-3 py-3">
                    <p className="text-gray-900 font-medium">{t.name}</p>
                    {t.description && <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">{t.description}</p>}
                    {t.is_system && <span className="inline-block mt-1 text-[10px] bg-gray-200 text-gray-700 px-1.5 py-[1px] rounded-sm uppercase tracking-wider">Sistema</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1 text-xs">
                      <Icon className="w-3 h-3 text-gray-500" />
                      {CATEGORY_LABEL[t.category] || t.category}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-700 max-w-[260px] truncate" title={t.subject}>{t.subject}</td>
                  <td className="px-3 py-3 text-xs">
                    {t.variables.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {t.variables.slice(0, 3).map((v) => (
                          <code key={v} className="text-[10px] bg-gray-100 px-1.5 py-[1px] rounded-sm">{`{{${v}}}`}</code>
                        ))}
                        {t.variables.length > 3 && <span className="text-[10px] text-gray-500">+{t.variables.length - 3}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right text-xs text-gray-700">{t.used_count}</td>
                  <td className="px-3 py-3 text-center">
                    <Switch
                      checked={t.is_active}
                      onCheckedChange={() => toggleActive(t)}
                      disabled={t.is_system}
                      data-testid={`template-toggle-${t.id}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      <Link to={`/admin/emails/templates/preview/${t.id}`} title="Designer visual" data-testid={`template-designer-${t.id}`}>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-brand-burgundy">
                          <Wand2 className="w-3 h-3" />
                        </Button>
                      </Link>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setPreviewing(t)} data-testid={`template-preview-${t.id}`}>
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setEditingId(t.id)} data-testid={`template-edit-${t.id}`}>
                        <Edit3 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => duplicate(t)} data-testid={`template-duplicate-${t.id}`}>
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-[11px] text-rose-700"
                        disabled={t.is_system}
                        onClick={() => setDeleting(t)}
                        data-testid={`template-delete-${t.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal edit/create */}
      {editingId !== null && (
        <TemplateEditorModal
          id={editingId === "new" ? null : editingId}
          parts={parts}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); load(); }}
        />
      )}

      {/* Modal preview */}
      {previewing && (
        <TemplatePreviewModal
          template={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}

      {/* Confirm delete */}
      <Dialog open={Boolean(deleting)} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent className="max-w-md" data-testid="template-delete-modal">
          <DialogHeader>
            <DialogTitle>Eliminar plantilla</DialogTitle>
            <DialogDescription>
              ¿Eliminar la plantilla <strong>"{deleting?.name}"</strong>?
              Esta acción no se puede deshacer (los emails ya enviados con esta plantilla no se afectan).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} data-testid="template-delete-cancel">Cancelar</Button>
            <Button className="bg-rose-700 hover:bg-rose-800 text-white" onClick={doDelete} data-testid="template-delete-confirm">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}


/**
 * TemplateEditorModal — crear o editar plantilla.
 */
function TemplateEditorModal({ id, parts, onClose, onSaved }) {
  const isNew = !id;
  const [form, setForm] = useState({
    name: "", description: "", category: "both",
    subject: "", html: "", is_active: true,
    header_id: "_default_", footer_id: "_default_",  // "_default_" | "_none_" | "<part_id>"
    use_default_header: true, use_default_footer: true,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [variables, setVariables] = useState([]);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/emails/templates/${id}`);
        setForm({
          name: data.name, description: data.description || "",
          category: data.category, subject: data.subject,
          html: data.html, is_active: data.is_active,
          header_id: data.header_id ? data.header_id : (data.use_default_header ? "_default_" : "_none_"),
          footer_id: data.footer_id ? data.footer_id : (data.use_default_footer ? "_default_" : "_none_"),
          use_default_header: data.use_default_header,
          use_default_footer: data.use_default_footer,
        });
        setIsSystem(data.is_system);
        setVariables(data.variables || []);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando plantilla");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, onClose]);

  // Recalcular variables en cliente para preview en vivo
  useEffect(() => {
    const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    const found = new Set();
    let m;
    while ((m = re.exec(form.subject || "")) !== null) found.add(m[1]);
    while ((m = re.exec(form.html || "")) !== null) found.add(m[1]);
    setVariables(Array.from(found).sort());
  }, [form.subject, form.html]);

  const save = async () => {
    if (form.name.trim().length < 3) { toast.error("El nombre debe tener al menos 3 caracteres"); return; }
    if (form.subject.trim().length < 1) { toast.error("El asunto es requerido"); return; }
    if (form.html.trim().length < 1) { toast.error("El cuerpo HTML es requerido"); return; }
    setSaving(true);
    try {
      // Mapear selección de header/footer a campos backend
      const partFields = {
        header_id: form.header_id === "_default_" || form.header_id === "_none_" ? null : form.header_id,
        use_default_header: form.header_id === "_default_",
        footer_id: form.footer_id === "_default_" || form.footer_id === "_none_" ? null : form.footer_id,
        use_default_footer: form.footer_id === "_default_",
      };
      if (isNew) {
        await api.post("/admin/emails/templates", {
          name: form.name.trim(), description: form.description.trim() || null,
          category: form.category, subject: form.subject, html: form.html,
          is_active: form.is_active, ...partFields,
        });
        toast.success("Plantilla creada");
      } else {
        await api.patch(`/admin/emails/templates/${id}`, {
          name: form.name.trim(), description: form.description.trim() || null,
          category: form.category, subject: form.subject, html: form.html,
          is_active: form.is_active, ...partFields,
        });
        toast.success("Plantilla actualizada");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nueva plantilla" : "Editar plantilla"}</DialogTitle>
          <DialogDescription className={isSystem ? "text-amber-700" : "text-gray-500"}>
            {isSystem
              ? "Esta es una plantilla del sistema. No se puede renombrar ni eliminar."
              : "Define el asunto, cuerpo HTML y variables {{name}}. Las variables se detectan automáticamente."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-gray-500 py-6">Cargando…</p>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Nombre *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={isSystem}
                  className="rounded-sm h-9"
                  data-testid="template-form-name"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="template-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compose">Solo Composer (envíos manuales)</SelectItem>
                    <SelectItem value="sequence">Solo Secuencias (automáticas)</SelectItem>
                    <SelectItem value="both">Ambos contextos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Descripción (opcional)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Para qué sirve esta plantilla…"
                className="rounded-sm h-9"
                data-testid="template-form-description"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Asunto *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder='Ej: Hola {{user_name}}, ...'
                className="rounded-sm h-9 font-mono text-sm"
                data-testid="template-form-subject"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Cuerpo HTML *</Label>
              <p className="text-[11px] text-gray-500 mb-1">
                Usa <code>{`{{variable_name}}`}</code> para placeholders. Las variables disponibles se muestran abajo.
              </p>
              <div className="border border-gray-300 rounded-sm" data-testid="template-form-html">
                <ReactQuill
                  theme="snow"
                  value={form.html}
                  onChange={(v) => setForm({ ...form, html: v })}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ color: [] }, { background: [] }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link"],
                      ["clean"],
                    ],
                  }}
                  style={{ minHeight: 220 }}
                />
              </div>
            </div>

            {variables.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 p-2 rounded-sm">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1">
                  <Variable className="w-3 h-3" /> Variables detectadas
                </p>
                <div className="flex flex-wrap gap-1">
                  {variables.map((v) => (
                    <code key={v} className="text-[11px] bg-white border border-gray-300 px-2 py-0.5 rounded-sm">{`{{${v}}}`}</code>
                  ))}
                </div>
              </div>
            )}

            {/* Iter14 — Encabezado / pie reutilizables */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-200">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Encabezado</Label>
                <Select value={form.header_id} onValueChange={(v) => setForm({ ...form, header_id: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="template-form-header">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default_">— Usar default activo —</SelectItem>
                    <SelectItem value="_none_">— Sin encabezado —</SelectItem>
                    {(parts?.header || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Pie</Label>
                <Select value={form.footer_id} onValueChange={(v) => setForm({ ...form, footer_id: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="template-form-footer">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default_">— Usar default activo —</SelectItem>
                    <SelectItem value="_none_">— Sin pie —</SelectItem>
                    {(parts?.footer || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="template-form-active"
              />
              <Label className="text-xs">Plantilla activa (disponible para usar)</Label>
            </div>
          </div>
        )}
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="template-form-cancel">Cancelar</Button>
          <Button
            className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
            disabled={saving || loading}
            onClick={save}
            data-testid="template-form-save"
          >
            {saving ? "Guardando…" : (isNew ? "Crear plantilla" : "Guardar cambios")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * TemplatePreviewModal — preview con valores sample editables.
 */
function TemplatePreviewModal({ template, onClose }) {
  const [sampleValues, setSampleValues] = useState(() => {
    const init = {};
    template.variables.forEach((v) => { init[v] = `[${v}]`; });
    return init;
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const render = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/emails/templates/${template.id}/preview`, {
        sample_values: sampleValues,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error generando preview");
    } finally {
      setLoading(false);
    }
  }, [template.id, sampleValues]);

  useEffect(() => { render(); }, [render]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-preview-modal">
        <DialogHeader>
          <DialogTitle>Preview — {template.name}</DialogTitle>
          <DialogDescription>
            Sustituye las variables con valores de ejemplo para ver el render final.
          </DialogDescription>
        </DialogHeader>

        {template.variables.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 p-3 rounded-sm space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Valores de ejemplo</p>
            <div className="grid grid-cols-2 gap-2">
              {template.variables.map((v) => (
                <div key={v}>
                  <Label className="text-[10px] text-gray-600 font-mono">{`{{${v}}}`}</Label>
                  <Input
                    value={sampleValues[v] || ""}
                    onChange={(e) => setSampleValues({ ...sampleValues, [v]: e.target.value })}
                    className="rounded-sm h-8 text-xs"
                    data-testid={`template-preview-input-${v}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-2 bg-gray-50">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Asunto</p>
            <p className="text-sm font-medium text-gray-900" data-testid="template-preview-subject">
              {loading ? "…" : (preview?.subject || "—")}
            </p>
          </div>
          <div className="p-4 max-h-[400px] overflow-y-auto" data-testid="template-preview-body">
            {loading ? (
              <p className="text-sm text-gray-500">Generando preview…</p>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: preview?.html || "" }} />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="template-preview-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/**
 * PartsManager — CRUD de headers y footers reutilizables.
 */
function PartsManager({ onReload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {part:obj, kind}
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emails/parts");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando parts");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/emails/parts/${p.id}`, { is_active: !p.is_active });
      await load(); onReload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    }
  };

  const setDefault = async (p) => {
    try {
      await api.patch(`/admin/emails/parts/${p.id}`, { is_default: true });
      toast.success(`${p.name} ahora es el ${p.kind} default`);
      await load(); onReload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/emails/parts/${deleting.id}`);
      toast.success("Eliminado");
      setDeleting(null);
      await load(); onReload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    }
  };

  const headers = items.filter((p) => p.kind === "header");
  const footers = items.filter((p) => p.kind === "footer");

  const Section = ({ kind, title, list }) => (
    <div className="mb-6" data-testid={`parts-section-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        <Button
          size="sm"
          variant="outline"
          className="rounded-sm h-8"
          onClick={() => setEditing({ part: null, kind })}
          data-testid={`part-new-${kind}`}
        >
          <Plus className="w-3 h-3 mr-1" /> Nuevo {kind === "header" ? "encabezado" : "pie"}
        </Button>
      </div>
      <div className="border border-gray-200 bg-white">
        {list.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-gray-500">
            Sin {kind === "header" ? "encabezados" : "pies"} creados.
          </p>
        )}
        {list.map((p) => (
          <div key={p.id} className="border-b border-gray-100 px-3 py-2 flex items-center gap-2" data-testid={`part-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900">
                {p.name}
                {p.is_default && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-[1px] rounded-sm">default</span>}
                {p.is_system && <span className="ml-2 text-[10px] uppercase tracking-wider bg-gray-200 text-gray-700 px-1.5 py-[1px] rounded-sm">sistema</span>}
              </p>
            </div>
            <Switch
              checked={p.is_active}
              onCheckedChange={() => toggleActive(p)}
              data-testid={`part-toggle-${p.id}`}
            />
            {!p.is_default && p.is_active && (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setDefault(p)} data-testid={`part-set-default-${p.id}`}>
                Hacer default
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing({ part: p, kind })} data-testid={`part-edit-${p.id}`}>
              <Edit3 className="w-3 h-3" />
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-7 px-2 text-rose-700"
              disabled={p.is_system}
              onClick={() => setDeleting(p)}
              data-testid={`part-delete-${p.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="parts-manager">
      <p className="text-xs text-gray-600 mb-4">
        Los encabezados y pies son fragmentos HTML reutilizables que se anteponen y posponen al cuerpo de cada plantilla. Cada categoría tiene un <strong>default</strong> que se usa cuando una plantilla no especifica uno explícitamente.
      </p>
      {loading ? (
        <p className="text-sm text-gray-500">Cargando…</p>
      ) : (<>
        <Section kind="header" title="Encabezados" list={headers} />
        <Section kind="footer" title="Pies" list={footers} />
      </>)}

      {editing && (
        <PartEditorModal
          part={editing.part}
          kind={editing.kind}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onReload?.(); }}
        />
      )}

      <Dialog open={Boolean(deleting)} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent className="max-w-md" data-testid="part-delete-modal">
          <DialogHeader>
            <DialogTitle>Eliminar {deleting?.kind}</DialogTitle>
            <DialogDescription>¿Eliminar "{deleting?.name}"?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            <Button className="bg-rose-700 hover:bg-rose-800 text-white" onClick={doDelete} data-testid="part-delete-confirm">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PartEditorModal({ part, kind, onClose, onSaved }) {
  const isNew = !part;
  const [name, setName] = useState(part?.name || "");
  const [html, setHtml] = useState(part?.html || "");
  const [isDefault, setIsDefault] = useState(part?.is_default || false);
  const [isActive, setIsActive] = useState(part?.is_active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (name.trim().length < 3) { toast.error("El nombre debe tener al menos 3 caracteres"); return; }
    if (html.trim().length < 1) { toast.error("El HTML es requerido"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/emails/parts", {
          name: name.trim(), kind, html, is_default: isDefault, is_active: isActive,
        });
        toast.success("Creado");
      } else {
        await api.patch(`/admin/emails/parts/${part.id}`, {
          name: name.trim(), html, is_default: isDefault, is_active: isActive,
        });
        toast.success("Actualizado");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const kindLabel = kind === "header" ? "encabezado" : "pie";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="part-editor-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? `Nuevo ${kindLabel}` : `Editar ${kindLabel}`}</DialogTitle>
          <DialogDescription>
            Fragmento HTML que se {kind === "header" ? "antepone" : "pospone"} a cada plantilla que lo use.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">Nombre *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={part?.is_system}
              className="rounded-sm h-9"
              data-testid="part-form-name"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">HTML *</Label>
            <div className="border border-gray-300 rounded-sm" data-testid="part-form-html">
              <ReactQuill
                theme="snow"
                value={html}
                onChange={setHtml}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline"],
                    [{ color: [] }, { background: [] }],
                    ["link"], ["clean"],
                  ],
                }}
                style={{ minHeight: 200 }}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="part-form-active" />
              Activo
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <Switch checked={isDefault} onCheckedChange={setIsDefault} data-testid="part-form-default" />
              Marcar como default ({kindLabel})
            </label>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="part-form-cancel">Cancelar</Button>
          <Button
            className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
            disabled={saving}
            onClick={save}
            data-testid="part-form-save"
          >
            {saving ? "Guardando…" : (isNew ? "Crear" : "Guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
